<template>
    <BaseDialog v-model:open="open" :title="heading">
        <div class="flex flex-col gap-4" data-testid="sharing-intro-dialog">
            <div class="flex items-center gap-3 rounded-lgr bg-surface-sunken p-4">
                <span class="grid size-11 shrink-0 place-items-center rounded-xl bg-surface-raised text-ink-muted">
                    <i class="bi text-xl/none" :class="calloutIcon" aria-hidden="true"></i>
                </span>
                <p class="body-small text-ink-muted min-w-0">{{ calloutText }}{{ ` ${sharingDisclosure}` }}</p>
            </div>

            <p class="body text-ink-muted">
                <I18nT keypath="profile.collaboration.defaultSharing.changeAccessHint">
                    <template #button>
                        <span class="body-bold text-ink">{{ shareButtonText }}</span>
                    </template>
                </I18nT>
                <template v-if="alternativeAccessText">{{ ` ${alternativeAccessText}` }}</template>
            </p>
        </div>
        <template #footer>
            <BaseButton theme="primary" @click="open = false">{{ t("profile.collaboration.defaultSharing.gotIt") }}</BaseButton>
        </template>
    </BaseDialog>
</template>

<script setup lang="ts">
import { useProfileSharingCopy } from "~vue/CollaborationProfile/composables/useProfileSharingCopy";
import BaseButton from "~vue/components/BaseButton.vue";
import BaseDialog from "~vue/components/BaseDialog.vue";
import { ACCESS } from "~vue/utils/constants";
import { activeLocale } from "~vue/utils/formatDate";
import { computed } from "vue";
import { I18nT, useI18n } from "vue-i18n";

const { t } = useI18n();

const { sharingDisclosure, accessButtonLabel } = useProfileSharingCopy();

const { access, availableAccessLevels } = defineProps<{ access: ACCESS; availableAccessLevels: Set<string> }>();

const open = defineModel<boolean>("open", { default: false });

const heading = computed(
    () =>
        ({
            [ACCESS.ORGANIZATION]: t("profile.collaboration.defaultSharing.heading.organization"),
            [ACCESS.INVITED_ORG_MEMBERS]: t("profile.collaboration.defaultSharing.heading.invited"),
            [ACCESS.PRIVATE]: t("profile.collaboration.defaultSharing.heading.private"),
        })[access],
);

const calloutText = computed(
    () =>
        ({
            [ACCESS.ORGANIZATION]: t("profile.collaboration.sharing.initialOrganization"),
            [ACCESS.INVITED_ORG_MEMBERS]: t("profile.collaboration.sharing.initialInvited"),
            [ACCESS.PRIVATE]: t("profile.collaboration.sharing.initialPrivate"),
        })[access],
);

const CALLOUT_ICONS: Record<ACCESS, string> = {
    [ACCESS.ORGANIZATION]: "bi-people-fill",
    [ACCESS.INVITED_ORG_MEMBERS]: "bi-person-plus-fill",
    [ACCESS.PRIVATE]: "bi-lock-fill",
};

const calloutIcon = computed(() => CALLOUT_ICONS[access]);

const shareButtonText = computed(() => accessButtonLabel(access));

// Intl.ListFormat supplies the locale's own "or", so translations never stitch fragments by hand.
const alternativeAccessText = computed(() => {
    const fragments: Record<ACCESS, string> = {
        [ACCESS.INVITED_ORG_MEMBERS]: t("profile.collaboration.defaultSharing.alternatives.invited"),
        [ACCESS.ORGANIZATION]: t("profile.collaboration.defaultSharing.alternatives.organization"),
        [ACCESS.PRIVATE]: t("profile.collaboration.defaultSharing.alternatives.private"),
    };
    const options = [ACCESS.INVITED_ORG_MEMBERS, ACCESS.ORGANIZATION, ACCESS.PRIVATE]
        .filter((level) => level !== access && availableAccessLevels.has(level))
        .map((level) => fragments[level]);
    if (options.length === 0) {
        return "";
    }
    return t("profile.collaboration.defaultSharing.alternatives.sentence", {
        options: new Intl.ListFormat(activeLocale(), { type: "disjunction" }).format(options),
    });
});
</script>
